import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import VisuallyHidden from '@reach/visually-hidden';
import { useForkedRef } from "../../hooks";
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { theme, whenHovered } from "../../theme";
import { wrapEvent } from "../../utils/wrap-event";
import { useSharedHoverContext } from "../SharedHover/SharedHoverContext";
import { mergeProps } from '@react-aria/utils';
import { rectToObject } from "../../utils/rect-to-object";
import { hoverStyles } from "../Interactive/mixins";
import { baseIconButtonStyles, iconButtonGhostVariant } from "../Button/IconButton";
import { whenPressed } from "../../theme/utils";
export var IconToggle = React.forwardRef(function (_ref, forwardedRef) {
  var ariaExpandedProp = _ref['aria-expanded'],
      as = _ref.as,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'secondary' : _ref$color,
      icon = _ref.icon,
      isSelected = _ref.isSelected,
      label = _ref.label,
      onClickProp = _ref.onClick,
      onSelectedChange = _ref.onSelectedChange,
      _ref$selectedColor = _ref.selectedColor,
      selectedColor = _ref$selectedColor === void 0 ? 'brandPrimary' : _ref$selectedColor,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      other = _objectWithoutProperties(_ref, ["aria-expanded", "as", "color", "icon", "isSelected", "label", "onClick", "onSelectedChange", "selectedColor", "size"]); // Ensure all refs are forwarded to the button to support
  // things like tooltips and popovers.


  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);

  var _useSharedHoverContex = useSharedHoverContext(),
      onEnterTarget = _useSharedHoverContex.onEnterTarget,
      onLeaveTarget = _useSharedHoverContex.onLeaveTarget,
      onLockTarget = _useSharedHoverContex.onLockTarget,
      onUnlockTarget = _useSharedHoverContex.onUnlockTarget,
      enabled = _useSharedHoverContex.enabled;

  function onClick() {
    if (onSelectedChange) {
      onSelectedChange(!isSelected);
    }
  }

  function onMouseEnter(e) {
    onEnterTarget(rectToObject(e.currentTarget.getBoundingClientRect()));
  }

  function onMouseLeave() {
    onLeaveTarget();
  }

  useEffect(function () {
    if (!enabled) {
      return;
    }

    if (ariaExpandedProp) {
      var rect = rectToObject(ownRef.current.getBoundingClientRect());
      onLockTarget(rect); // eslint-disable-next-line consistent-return

      return function () {
        onUnlockTarget(rect);
      };
    }
  }, [ariaExpandedProp, enabled, onLockTarget, onUnlockTarget, size]);
  return /*#__PURE__*/React.createElement(StyledToggleIconButton, _extends({
    $color: color,
    $isSelected: isSelected,
    $selectedColor: selectedColor,
    $sharedHoverEnabled: enabled,
    $size: size,
    $willToggleOnClick: Boolean(onSelectedChange),
    "aria-expanded": ariaExpandedProp,
    "aria-pressed": isSelected,
    as: as,
    "data-testid": "icon-toggle",
    onClick: wrapEvent(onClick, onClickProp),
    ref: ref,
    role: "button"
  }, mergeProps({
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }, other)), /*#__PURE__*/React.createElement(VisuallyHidden, null, label), React.cloneElement(icon, {
    'aria-hidden': true,
    size: 24
  }));
});
var StyledToggleIconButton = styled.button.withConfig({
  displayName: "IconToggle__StyledToggleIconButton",
  componentId: "vapor__sc-1gmkxn1-0"
})(["", ";", ";", ";", " will-change:transform;", ";&[disabled]{color:", ";opacity:0.5;pointer-events:none;}"], baseIconButtonStyles, hoverStyles, iconButtonGhostVariant, function (p) {
  return p.$isSelected && "\n    color: ".concat(theme.colors.palette[p.$selectedColor], ";\n    ").concat(whenHovered, " {\n      color: ").concat(theme.colors.palette[p.$selectedColor], ";\n    }\n  ");
}, activeState, function (p) {
  return p.$isSelected ? theme.colors.palette[p.$selectedColor] : theme.colors.palette.grey40;
});

function activeState(_ref2) {
  var $willToggleOnClick = _ref2.$willToggleOnClick,
      $selectedColor = _ref2.$selectedColor,
      $isSelected = _ref2.$isSelected;

  if ($isSelected) {
    return css(["&[aria-expanded='true'],", "{color:", ";}"], whenPressed, theme.colors.palette[$selectedColor]);
  }

  if ($willToggleOnClick) {
    return css(["", "{color:", ";}"], whenPressed, theme.colors.palette[$selectedColor]);
  }

  return css([""]);
}